import React, { useState, useEffect } from 'react';
import useHttpApiCall from '../../../services/useHttpApiCall';
import Alert from 'react-bootstrap/Alert';
import { IoIosCloseCircle } from 'react-icons/io';
import { RiDraggable } from 'react-icons/ri';
import { IoCloseCircleOutline } from 'react-icons/io5';
import { useParams } from 'react-router-dom';
import { Modal, Button } from 'react-bootstrap';
import { markDashboardUpdated } from '../../../redux/myDashboard/dashboardSlice';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, rectSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useDispatch } from 'react-redux';
import '../style.css';

function SortableCard({ graph, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: graph.id
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  };

  const renderGraphComponent = () => {
    if (!graph.component)
      return <div className="no-component-message">No data available at this moment</div>;
    if (typeof graph.component === 'function') {
      const GraphComponent = graph.component;
      return <GraphComponent {...graph.config} />;
    }
    return graph.component;
  };

  return (
    <div className="card-wrapper">
      <div ref={setNodeRef} style={style} className="sortable-card">
        <div className="sortable-card-container">
          <div className="graph-component-container">
            {renderGraphComponent()}
            <div className="graph-block-overlay" />
          </div>

          <div className="graph-controls-container">
            <div {...attributes} {...listeners} title="Drag to reorder" className="drag-handle">
              <RiDraggable />
            </div>

            <span
              onClick={() => onRemove(graph.id)}
              className="remove-graph-button"
              title="Remove graph">
              <IoCloseCircleOutline />
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Panel({
  selectedGraphs,
  handleClose,
  onReorder,
  removeGraphFromDashboard,
  dashboardName: initialDashboardName
}) {
  const [graphs, setGraphs] = useState(selectedGraphs || []);
  const [dashboardName, setDashboardName] = useState(initialDashboardName || '');
  const [payload, setPayload] = useState(undefined);
  const [alertVisible, setAlertVisible] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertVariant, setAlertVariant] = useState('success');
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [limitExceeded, setLimitExceeded] = useState(false);
  const [limitMessage, setLimitMessage] = useState('');
  const [toastPosition, setToastPosition] = useState('top-right');
  const [inputError, setInputError] = useState('');

  //console.log("graps",graphs)
  const pathSegments = window.location.pathname.split('/');
  const isEditMode = pathSegments.includes('viewDashboardList');
  const { id } = useParams();

  const showToast = (message, variant, position = 'top-right') => {
    setAlertMessage(message);
    setAlertVariant(variant);
    setAlertVisible(true);
    setToastPosition(position);

    const timeout = variant === 'success' ? 4000 : 5000;
    setTimeout(() => {
      setAlertVisible(false);
    }, timeout);
  };
  const toastPositionStyles = {
    'top-right': { top: '50px', right: '250px' },
    'top-left': { top: '20px', left: '20px' },
    'bottom-right': { bottom: '60px', right: '20px' },
    'bottom-left': { bottom: '20px', left: '20px' }
  };

  const [data, loading, error] = useHttpApiCall(payload);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 }
    })
  );
  const dispatch = useDispatch();

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active?.id !== over?.id) {
      const oldIndex = graphs?.findIndex((g) => g.id === active?.id);
      const newIndex = graphs?.findIndex((g) => g?.id === over?.id);
      const reordered = arrayMove(graphs, oldIndex, newIndex);
      setGraphs(reordered);
      onReorder(reordered);
    }
  };

  const handleRemove = (id) => {
    removeGraphFromDashboard(id);
    setGraphs((prev) => prev.filter((g) => g.id !== id));
  };

  // Sort graphs based on graph_order
  useEffect(() => {
    if (selectedGraphs && selectedGraphs.length > 0) {
      const sortedGraphs = [...selectedGraphs].sort((a, b) => {
        const orderA = a.config?.graph_order || a.config?.processDetails?.graph_order;
        const orderB = b.config?.graph_order || b.config?.processDetails?.graph_order;
        return orderA - orderB;
      });
      setGraphs(sortedGraphs);
    } else {
      setGraphs([]);
    }
  }, [selectedGraphs]);

  useEffect(() => {
    if (isEditMode && initialDashboardName) {
      setDashboardName(initialDashboardName);
    }
  }, [isEditMode, initialDashboardName]);

  const handleSave = () => {
    if (!dashboardName.trim()) {
      setInputError('Please enter a dashboard name.');
      return;
    }
    setInputError(''); // clear error if validx
    const formattedGraphs = graphs.map((graph) => ({
      graph_template: graph.config?.graph_template || graph.config?.processDetails?.graph_template,
      query_params: graph.config?.query_params || graph.config?.processDetails?.query_params
    }));

    const requestData = {
      name: dashboardName.trim(),
      is_active: true,
      graphs: formattedGraphs
    };

    const apiUrl = isEditMode
      ? `v0/my-dashboard/${id}/` //PATCH
      : 'v0/my-dashboard/'; //post

    const method = isEditMode ? 'PATCH' : 'POST';

    setPayload({
      auth: true,
      url: apiUrl,
      method: method,
      data: requestData
    });
    dispatch(markDashboardUpdated()); // notify sidebar
  };

  useEffect(() => {
    if (data && !error) {
      if (data.data.status === 3) {
        setLimitMessage(
          data.data.comment
        );
        setLimitExceeded(true);

        setPayload(undefined);
        return;
      }
      showToast(
        isEditMode ? 'Dashboard updated successfully!' : 'Dashboard saved successfully!',
        'success',
        'top-right'
      );

      // Close the confirmation modal if open
      setShowCloseConfirm(false);

      // Close the panel after successful save
      setTimeout(() => {
        handleClose();
      }, 2000);

      if (!isEditMode) {
        graphs.forEach((g) => removeGraphFromDashboard(g.id));
        setDashboardName('');
        setGraphs([]);
      }

      setPayload(undefined);
    }

    if (error) {
      //console.log('nme err', error);

      if (error.response?.data?.non_field_errors) {
        setInputError(error.response.data.non_field_errors.join(', '));
      } else if (error.response?.data?.message) {
        setInputError(error.response.data.message);
      } else {
        setInputError('An error occurred while saving the dashboard');
      }

      // showToast(errorMessage, 'danger', "bottom-right");
      setPayload(undefined);
    }
  }, [data, error]);

  // Handle the close button click
  const handleCloseClick = () => {
    setShowCloseConfirm(true);
  };

  // Handle the actual close after confirmation
  const handleConfirmClose = () => {
    setShowCloseConfirm(false);
    handleClose();
  };

  // Handle save from the confirmation modal
  const handleSaveAndClose = () => {
    setShowCloseConfirm(false);
    handleSave();
  };

  return (
    <div>
      {alertVisible && (
        <div
          style={{
            position: 'fixed',
            zIndex: 9999,
            minWidth: '250px',
            ...toastPositionStyles[toastPosition] // 👈 controls position
          }}>
          <Alert
            variant={alertVariant}
            onClose={() => setAlertVisible(false)}
            dismissible
            className={isEditMode ? 'custom-edit-alert' : 'custom-alert'}>
            {alertMessage}
          </Alert>
        </div>
      )}

      <div className={isEditMode ? 'edit-preview-panel' : 'preview-panel'}>
        <div className="preview-header">
          <h4>{isEditMode ? 'Edit Dashboard' : 'Preview'}</h4>

          <button onClick={handleCloseClick} className="close-panel-button">
            <IoIosCloseCircle />
          </button>
        </div>

        <p className="preview-description">
          {isEditMode ? (
            <div className="edit-header">
              Edit, rearrange, remove graphs, add graphs or update the dahboard name in edit mode.
            </div>
          ) : (
            <div>
              Preview, rearrange or remove graphs from your dashboard. Finally, name your dashboard
              and save it.
            </div>
          )}
        </p>

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={graphs.map((g) => g.id)} strategy={rectSortingStrategy}>
            <div className="graphs-grid">
              {graphs.length > 0 ? (
                graphs.map((graph) => (
                  <SortableCard key={graph.id} graph={graph} onRemove={handleRemove} />
                ))
              ) : (
                <div className="no-graphs-message">
                  No graphs selected. Add graphs to see them here.
                </div>
              )}
            </div>
          </SortableContext>
        </DndContext>

        <div className="mt-4 d-flex justify-content-center align-items-center">
          <div className="d-flex flex-column w-50 me-3">
            {/* Always reserve space */}
            <div style={{ minHeight: '20px' }}>
              {inputError && <div className="text-danger small mb-1">{inputError}</div>}
            </div>

            <input
              type="text"
              placeholder="Enter dashboard name"
              value={dashboardName}
              onChange={(e) => setDashboardName(e.target.value)}
              className={`form-control ${inputError ? 'is-invalid' : ''}`}
            />
          </div>
          <div className="mt-4">
            <button onClick={handleSave} className="btn btn-grad" disabled={loading}>
              {loading
                ? isEditMode
                  ? 'Updating...'
                  : 'Saving...'
                : isEditMode
                ? 'Update Dashboard'
                : 'Save Dashboard'}
            </button>
          </div>
        </div>
      </div>

      {/* Close Confirmation Modal */}
      <Modal show={showCloseConfirm} onHide={() => setShowCloseConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Unsaved Changes</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>You have unsaved changes. Do you want to save your dashboard before closing?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleConfirmClose}>
            Discard Changes
          </Button>
          <Button variant="danger" className="btn-grad" onClick={handleSaveAndClose}>
            Save and Close
          </Button>
        </Modal.Footer>
      </Modal>
      {/* Limit Exceeded Modal */}
      <Modal show={limitExceeded} onHide={() => setLimitExceeded(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Dashboard Limit Reached</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className='text-center'>{limitMessage}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setLimitExceeded(false)}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
